import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

interface CameraScannerProps {
  // Called with the raw decoded text on every successful QR decode.
  onScan: (decodedText: string) => void;
  // When true, decodes are not delivered to onScan. The parent pauses for
  // ~1.5s after each result so the same badge is not read repeatedly.
  paused: boolean;
}

// The DOM element html5-qrcode renders the camera stream into.
const SCANNER_ELEMENT_ID = "camera-scanner-region";

// E2E injection seam. A headless browser has no camera, so the Playwright
// integration suite dispatches a `window` CustomEvent carrying a decoded
// badge string instead of presenting a QR code. The handler routes that
// string through the exact same path a real decode takes (it respects the
// `paused` prop). This is inert in production — nothing in the app or build
// dispatches this event; it is purely a test hook.
const E2E_SCAN_EVENT = "attendik:e2e-scan";

// Wraps the html5-qrcode library. Starts the camera on mount, stops and
// cleans up on unmount. Decodes are suppressed while `paused`.
export function CameraScanner({ onScan, paused }: CameraScannerProps): JSX.Element {
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Refs so the long-lived scanner callback always sees current values
  // without restarting the camera.
  const pausedRef = useRef(paused);
  const onScanRef = useRef(onScan);
  pausedRef.current = paused;
  onScanRef.current = onScan;

  // Shared decode path: used by both the real camera and the E2E seam so a
  // simulated scan behaves identically to a genuine one (it honours `paused`).
  const handleDecode = (decodedText: string): void => {
    if (pausedRef.current) {
      return;
    }
    onScanRef.current(decodedText);
  };
  const handleDecodeRef = useRef(handleDecode);
  handleDecodeRef.current = handleDecode;

  // E2E injection seam — see E2E_SCAN_EVENT above. Inert in production.
  useEffect(() => {
    const onE2eScan = (event: Event): void => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (typeof detail === "string") {
        handleDecodeRef.current(detail);
      }
    };
    window.addEventListener(E2E_SCAN_EVENT, onE2eScan);
    return () => {
      window.removeEventListener(E2E_SCAN_EVENT, onE2eScan);
    };
  }, []);

  useEffect(() => {
    let scanner: Html5Qrcode | null = null;
    let cancelled = false;

    async function start(): Promise<void> {
      try {
        scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
        await scanner.start(
          { facingMode: "environment" },
          // CR-07: constrain the decoder to a centred square that matches
          // the four white corner brackets ScanPage draws (≈70% of the
          // smaller dimension). Operators read the brackets as the scan
          // window — pre-CR-07 the brackets were cosmetic and a
          // neighbour's QR at the edge of the frame could be picked up.
          //
          // Sizing on min(videoWidth, videoHeight) keeps the qrbox a true
          // square on any orientation. CR-03 disabled qrbox because the
          // library draws it in video-pixel space; matching the smaller
          // dimension prevents the rectangle-stretch artefact CR-03 hit.
          {
            fps: 10,
            qrbox: (videoWidth: number, videoHeight: number) => {
              const side = Math.floor(
                Math.min(videoWidth, videoHeight) * 0.7
              );
              return { width: side, height: side };
            }
          },
          handleDecode,
          // Per-frame decode failures are normal noise — ignore them.
          undefined
        );
        if (cancelled) {
          // Unmounted while starting — tear the camera back down.
          await scanner.stop();
          scanner.clear();
        }
      } catch {
        if (!cancelled) {
          setPermissionDenied(true);
        }
      }
    }

    void start();

    return () => {
      cancelled = true;
      const active = scanner;
      if (!active) {
        return;
      }
      // html5-qrcode's stop() throws *synchronously* (not a rejected promise)
      // when the camera never started — e.g. permission denied or no device.
      // The try/catch guards that throw; .catch() guards the async rejection.
      try {
        void active
          .stop()
          .then(() => active.clear())
          .catch(() => undefined);
      } catch {
        // Scanner was never running — nothing to tear down.
      }
    };
  }, []);

  if (permissionDenied) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-900 p-6 text-center">
        <p className="text-lg font-medium text-white">
          Camera access required. Please allow camera access and reload.
        </p>
      </div>
    );
  }

  return (
    <div
      id={SCANNER_ELEMENT_ID}
      data-testid="camera-scanner"
      className="h-full w-full bg-black"
    />
  );
}
